import { parseJSON } from '@rsos/utils';
import {
  MEDICALERT,
  MEDICALERT_CA,
  MEDICALERT_UK,
} from '../../actions/fetchEmergencySession';
import { wait } from '../../utils/wait';

type ADRPayload = Record<
  string,
  { data: Record<string, unknown> | null } | null
>;

const RETRY_BACKOFF_DELAYS_MS = [1000, 1000, 2000, 2000, 3000];

/**
 * @function fetchRetryWithADR checks the scheme of searchTerm
 * attempts the given fetch request recursively until
 * the limit is reached or ADR orgName is found
 */
function fetchRetryWithADR(
  url: string,
  options: RequestInit,
  scheme: string,
): Promise<Response> {
  const shouldRetry = scheme === 'tel' || isMafidScheme(scheme);

  if (shouldRetry) {
    const doRecursiveRequest = (
      reqUrl: string,
      reqOptions: RequestInit,
      retryIndex = 0,
    ): Promise<Response> => {
      return fetch(reqUrl, reqOptions).then(response =>
        (parseJSON(response.clone()) as unknown as Promise<unknown>).then(
          rawPayload => {
            const parsedPayload = (rawPayload || {}) as ADRPayload;
            const foundData =
              scheme === 'tel'
                ? hasTelData(parsedPayload)
                : hasMafidData(parsedPayload);

            if (foundData || retryIndex >= RETRY_BACKOFF_DELAYS_MS.length) {
              return response;
            }

            return wait(RETRY_BACKOFF_DELAYS_MS[retryIndex]).then(() =>
              doRecursiveRequest(reqUrl, reqOptions, retryIndex + 1),
            );
          },
        ),
      );
    };

    return doRecursiveRequest(url, options);
  }

  return fetch(url, options);
}

// fetchEmergencySession may rewrite "maf-id" to regional variants
// ("maf-id-ca", "maf-id-uk"), so retry checks must handle all maf-id* schemes.
function isMafidScheme(scheme: string): boolean {
  return `${scheme}`.startsWith('maf-id');
}

function hasMafidData(payload: ADRPayload): boolean {
  return [MEDICALERT, MEDICALERT_CA, MEDICALERT_UK].some(orgName => {
    const data = payload?.[orgName]?.data;
    return !!data && Object.keys(data).length > 0;
  });
}

function hasTelData(payload: ADRPayload): boolean {
  for (const orgName in payload) {
    if (
      orgName === MEDICALERT ||
      orgName === MEDICALERT_CA ||
      orgName === MEDICALERT_UK
    ) {
      continue;
    }
    return true;
  }
  return false;
}

export default fetchRetryWithADR;
